import moment from 'moment';

/**
 * Formats a JavaScript Date object to a string in the given format (default: 'MMM DD YYYY').
 * Always uses local time.
 * @param date - The Date object to format.
 * @param format - The desired output format (default: 'MMM DD YYYY').
 * @returns The formatted date string.
 */
export const formatDate = (date: Date, format = 'MMM DD YYYY'): string => {
  return moment(date).format(format); // local time
};

export const toISODateLocal = (d: Date | null): string | null => {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`; // "2000-05-26"
};

export function parseYMDToLocalDate(ymd?: string | null): Date | undefined {
  if (!ymd) return undefined;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return undefined;
  const y = Number(m[1]),
    mo = Number(m[2]) - 1,
    d = Number(m[3]);
  const date = new Date(y, mo, d); // local midnight
  return isNaN(+date) ? undefined : date;
}

/**
 * Converts a date string in 'YYYY-MM-DD' format to a JavaScript Date object in local time.
 * This avoids timezone issues when displaying or editing dates in forms (e.g., driver forms).
 * @param date - The date string in 'YYYY-MM-DD' format.
 * @returns The corresponding Date object in local time, or null if input is null/empty.
 */
export const toDate = (date: string | null): Date | null => {
  if (!date) return null;
  return moment(date, 'YYYY-MM-DD').toDate();
};

/**
 * Returns the current date as a string in 'YYYY-MM-DD' format (local time).
 * @returns The current date string.
 */
export const currentDate = (): string => formatDate(new Date(), 'YYYY-MM-DD');

/**
 * Formats a date string or Date object to a US date format (MMM DD, YYYY) in UTC.
 * Intended for displaying company-related dates fetched from the backend.
 * @param date - The date string or Date object to format.
 * @returns The formatted date string in UTC.
 */
export const formatToUSDate = (date: string | Date): string => {
  const parsedDate = typeof date === 'string' ? moment.utc(date) : moment.utc(date.toISOString());
  return parsedDate.format('MMM DD, YYYY'); // Format as UTC
};

/**
 * Converts a Date object to an ISO string (YYYY-MM-DD) or null.
 * @param d - The Date object to convert.
 * @returns The ISO string or null.
 */
export const toISO = (d: Date | null): string | null => (d ? formatDate(d, 'YYYY-MM-DD') : null);

/**
 * Parses an ISO string (YYYY-MM-DD) into a Date object or returns null if invalid.
 * @param iso - The ISO string to parse.
 * @returns The Date object or null.
 */
export const fromISO = (iso: string | null): Date | null => {
  const parsed = iso ? toDate(iso) : null;
  return parsed && !isNaN(parsed.getTime()) ? parsed : null;
};

/**
 * Formats a Date object to MM/DD/YYYY or returns an empty string if null.
 * @param d - The Date object to format.
 * @returns The formatted string.
 */
export const formatMMDDYYYY = (d: Date | null): string => (d ? formatDate(d, 'MM/DD/YYYY') : '');

/**
 * Returns a max date based on the current date and the provided range in days.
 * @param {number} rangeInDays - The number of days to add to the current date.
 * @returns {Date} - The calculated max date.
 */
export const getMaxDate = (rangeInDays: number): Date => {
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + rangeInDays);
  maxDate.setHours(23, 59, 59, 999); // Set to the end of the day
  return maxDate;
};

export const getMaxDateEndOfNextYear = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear() + 1, 11, 31, 23, 59, 59, 999);
};
